import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { SessionCheckFailed, SessionChecking } from "./components/shared/session-check";
import { requireCaptureAuth } from "./lib/offline-session";
import { requireAdmin, requireAuth, requirePm } from "./lib/route-guards";
import { reportStudioError } from "./lib/studio-inspector";
import { RootLayout } from "./routes/__root";
import { CaptureHomePage, CaptureLayout } from "./routes/capture";
import { DashboardPage } from "./routes/dashboard/index";
import { LoginPage } from "./routes/login";
import { ProjectPage } from "./routes/project";
import { SignupPage } from "./routes/signup";
import { projectSearchSchema } from "./schemas/photos";

// Pages most visitors never open (profile, and the admin-only settings screens)
// load on demand: each `lazyRouteComponent` import becomes its own chunk in the
// production build, which the router fetches before the route renders. The
// landing pages (login, signup, dashboard) stay in the main bundle so a first
// visit doesn't wait on a second request. A new rarely-visited page should
// follow the same pattern.
const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  pendingComponent: SessionChecking,
  errorComponent: SessionCheckFailed,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const projectRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects/$projectId",
  validateSearch: (search) => projectSearchSchema.parse(search),
  component: ProjectPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: lazyRouteComponent(() => import("@/routes/dashboard/profile"), "ProfilePage"),
});

const migrationRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/migration",
  beforeLoad: async () => {
    await requirePm();
  },
  component: lazyRouteComponent(() => import("@/routes/migration"), "MigrationPage"),
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: lazyRouteComponent(
    () => import("@/routes/dashboard/settings/users"),
    "SettingsUsersPage",
  ),
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: lazyRouteComponent(
    () => import("@/routes/dashboard/settings/system"),
    "SettingsSystemPage",
  ),
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: lazyRouteComponent(
    () => import("@/routes/dashboard/settings/email"),
    "SettingsEmailPage",
  ),
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: lazyRouteComponent(
    () => import("@/routes/dashboard/settings/audit"),
    "SettingsAuditPage",
  ),
});

// The field capture app: phone screens with no sidebar. Its guard lets a remembered PM in
// with no signal (`src/lib/offline-session.ts`). The frame and home are static (the installed
// app opens on them); the frame fetches the other screens as soon as it is up.
const captureLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/capture",
  beforeLoad: requireCaptureAuth,
  pendingComponent: SessionChecking,
  errorComponent: SessionCheckFailed,
  component: CaptureLayout,
});

const captureHome = createRoute({
  getParentRoute: () => captureLayout,
  path: "/",
  component: CaptureHomePage,
});

const captureProject = createRoute({
  getParentRoute: () => captureLayout,
  path: "/projects/$projectId",
  component: lazyRouteComponent(() => import("@/routes/capture-project"), "CaptureProjectPage"),
});

const captureAlbum = createRoute({
  getParentRoute: () => captureLayout,
  path: "/projects/$projectId/albums/$albumId",
  component: lazyRouteComponent(() => import("@/routes/capture-album"), "CaptureAlbumPage"),
});

const captureQueue = createRoute({
  getParentRoute: () => captureLayout,
  path: "/queue",
  component: lazyRouteComponent(() => import("@/routes/capture-queue"), "CaptureQueuePage"),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    projectRoute,
    profileRoute,
    migrationRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
    ]),
  ]),
  captureLayout.addChildren([captureHome, captureProject, captureAlbum, captureQueue]),
]);

export const router = createRouter({ routeTree, defaultOnCatch: reportStudioError });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
